import createStore from 'react-waterfall';
import Storage from 'local-storage-fallback';
import { Emitter } from '../authentication';

export default (typeKey, fetchExternal, idProperty = 'uuid', defaultList = [], additionalActionCreators = {}) => {

  /* --------- HELPERS --------- */
  const getId = object => object[idProperty];

  const getFreshness = state => {
    if (typeof state.lastFetched !== 'number') {
      return false;
    }
    return Date.now() - state.lastFetched;
  };

  const isSuperFresh = state => {
    const difference = getFreshness(state);
    if (difference === false) {
      return false;
    }
    // If we fetched less than 15 seconds ago, then we're good to go
    return difference < (15 * 1000);
  };

  const isFreshEnough = state => {
    const difference = getFreshness(state);
    if (difference === false) {
      return false;
    }
    // If we fetched less than 10 minutes ago, then we're good to go
    return difference < (10 * 60 * 1000);
  };

  /* --------- STATE & Caching state --------- */

  const getFromCache = (key) => {
    try {
      const value = Storage.getItem(`rewards.${key}`);
      if (typeof value !== 'string') {
        return null;
      }
      return JSON.parse(value);
    } catch(e) {
      return null;
    }
  };

  const setInCache = (key, value) => {
    try {
      Storage.setItem(`rewards.${key}`, JSON.stringify(value));
    } catch(e) {

    }
  };

  const getInitialState = () => {
    const cachedState = getFromCache(typeKey);
    if (cachedState && cachedState.list) {
      return cachedState;
    }
    return {
      loading: false,
      list: defaultList,
      lastFetched: null,
      isFresh: false
    };
  };

  let currentState = getInitialState();

  /* --------- ACTIONS --------- */

  const updateCache = (state, actions, object) => {
    // List here is cloned, so we can mutate
    const list = state.list.slice(),
      id = getId(object),
      index = list.findIndex(other => getId(other) === id);
    if (index === -1) {
      list.push(object);
    } else {
      list[index] = object;
    }
    return {
      isFresh: false,
      list
    };
  };

  const removeCached = (state, actions, id) => ({
    isFresh: false,
    list: state
      .list
      .filter(other => getId(other) !== id)
  });

  const setLoading = (unused1, unused2, loading) => ({
    loading
  });

  // Only fetch one at a time
  const fetch = async (state, actions) => {
    // If we just fetched, lets just return
    if (state.isFresh && isSuperFresh(state)) {
      return state.list;
    }
    let list;
    try {
      await actions.setLoading(true);
      list = await fetchExternal();
    } catch(e) {
      console.warn(`Failed to fetch external list for ${typeKey}`);
      console.warn(e);
      // Catch failed state and reset loading to false
      await actions.setLoading(false);
      throw e;
    }
    return {
      list,
      loading: false,
      isFresh: true,
      lastFetched: Date.now()
    };
  };

  const actionsCreators = {
    setLoading,
    updateCache,
    removeCached,
    fetch,
    ...additionalActionCreators
  };

  const fetchViaActions = () => {
    try {
      // If we aren't initialised yet, this will throw
      // If provider isn't initialised, then we will receive null instead of a promise
      Promise.resolve(
        store
          .actions
          .fetch()
      )
        .catch(console.error);
    } catch(e) {
      console.warn('Unable to fetch initial data', e);
    }
  };

  /* --------- Store creation & auto re-fetch --------- */
  const store = createStore(
    {
      initialState: currentState,
      actionsCreators
    },
    [
      () => {
        // Middleware will be invoked _before_ the provider is finished setting up
        // Because of this we want to wait a little bit before checking
        // 20ms isn't going to make a difference to the UI...
        setTimeout(fetchViaActions, 20);
        // Return a no-op
        return () => {};
      }
    ]
  );

  store.subscribe((unused, stateUpdate) => setInCache(typeKey, currentState = {
    ...currentState,
    ...stateUpdate
  }));

  Emitter.on('authenticated', () => {
    // We authenticated, lets go get some data
    fetchViaActions();
  });

  return store;
};


// WEBPACK FOOTER //
// ./src/services/collection/create.js